import React from "react";
import {
  Modal,
  Box,
  Typography,
  Grid,
  IconButton,
  Paper,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
} from "@mui/material";
import ReactDOMServer from "react-dom/server";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import PrintIcon from "@mui/icons-material/Print";
import PrintableEnvironmentalAssessment from "./PrintableEnvironmentalAssessment";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95%", sm: "90%", md: "1000px", lg: "1200px" },
  bgcolor: "background.paper",
  borderRadius: 3,
  boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
  p: 0,
  maxHeight: "95vh",
  overflowY: "auto",
  border: "1px solid #e5e7eb",
};

const headerStyle = {
  bgcolor: "#1976d2",
  color: "white",
  p: 3,
  position: "relative",
  borderBottom: "1px solid #e5e7eb",
};

const contentStyle = {
  p: 4,
  bgcolor: "#fafafa",
};

const EnvironmentalAssessmentDetailsModal = ({
  open,
  handleClose,
  assessment,
  questions = [],
}) => {
  if (!assessment) return null;

  const getAnswerIcon = (answer) => {
    switch (answer) {
      case "YES":
        return <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 20 }} />;
      case "NO":
        return <CancelIcon sx={{ color: "#f44336", fontSize: 20 }} />;
      case "NA":
        return <RemoveCircleIcon sx={{ color: "#9e9e9e", fontSize: 20 }} />;
      default:
        return <RemoveCircleIcon sx={{ color: "#9e9e9e", fontSize: 20 }} />;
    }
  };

  const getAnswerChip = (answer) => {
    const colors = {
      YES: { bgcolor: "#e8f5e8", color: "#2e7d32" },
      NO: { bgcolor: "#ffebee", color: "#c62828" },
      NA: { bgcolor: "#f5f5f5", color: "#616161" },
    };

    return (
      <Chip
        label={answer}
        size="small"
        sx={{
          ...colors[answer],
          fontWeight: 600,
          minWidth: 50,
        }}
      />
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not specified";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const handlePrint = () => {
    if (!assessment) return;

    try {
      const printableHtmlContent = ReactDOMServer.renderToString(
        <PrintableEnvironmentalAssessment
          assessment={assessment}
          questions={questions}
        />
      );

      const styleMatch = printableHtmlContent.match(
        /<style>([\s\S]*?)<\/style>/
      );
      const embeddedStyles = styleMatch ? styleMatch[1] : "";

      const contentWithoutStyle = printableHtmlContent.replace(
        /<style>[\s\S]*?<\/style>/,
        ""
      );

      // Create a hidden iframe
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      document.body.appendChild(iframe);

      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Environmental Assessment Report - ${assessment.client_name}</title>
          <style>
            @page {
              size: A4 portrait;
              margin: 1cm;
            }
            body {
              margin: 0;
              padding: 0;
            }
            ${embeddedStyles}
          </style>
        </head>
        <body>
          ${contentWithoutStyle}
        </body>
      </html>
    `);
      doc.close();

      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        iframe.contentWindow.onafterprint = () => {
          document.body.removeChild(iframe);
        };
      };
    } catch (error) {
      console.error("Error preparing for print:", error);
      alert("Failed to prepare the report for printing. Please try again.");
    }
  };

  // Create a map of answered questions for quick lookup
  const answeredQuestions = new Map(
    assessment.answers.map((answer) => [answer.question_id, answer])
  );

  // Merge all questions with their answers (if any)
  const allQuestionsWithAnswers = questions.map((question, index) => {
    const answer = answeredQuestions.get(question.id);
    return {
      ...question,
      questionNumber: index + 1,
      answer: answer?.answer || "",
      note: answer?.note || "",
      isAnswered: Boolean(answer),
    };
  });

  const totalQuestions = questions.length;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="assessment-details-modal-title"
    >
      <Box sx={modalStyle}>
        <Box sx={headerStyle}>
          <Typography
            id="assessment-details-modal-title"
            variant="h5"
            component="h2"
            fontWeight={600}
          >
            Environmental Risk Assessment Details
          </Typography>

          <Box
            sx={{
              position: "absolute",
              right: 16,
              top: 16,
              display: "flex",
              gap: 1,
            }}
          >
            <IconButton
              aria-label="print"
              onClick={handlePrint}
              sx={{ color: "white" }}
            >
              <PrintIcon />
            </IconButton>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{ color: "white" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Box sx={contentStyle}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              backgroundColor: "#fafafa",
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Assessment Information
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                mb: 2,
              }}
            >
              {[
                { label: "Client Name", value: assessment.client_name },
                { label: "Assessor Name", value: assessment.assessor_name },
                {
                  label: "Date Completed",
                  value: formatDate(assessment.date_completed),
                },
                {
                  label: "Next Review Date",
                  value: formatDate(assessment.next_review_date),
                },
                {
                  label: "Assessment Address",
                  value: assessment.address || "Not specified",
                },
              ].map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    flex: "1 1 18%", // 5 columns across
                    minWidth: "180px",
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {item.label}
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary">
                Assessor Signature
              </Typography>
              <Box
                sx={{
                  mt: 1,
                  p: 0.5,
                  border: "1px solid #ccc",
                  borderRadius: 1,
                  width: 120,
                  bgcolor: "#fff",
                }}
              >
                <img
                  src={assessment?.assessor_signature}
                  alt="Signature"
                  style={{ width: "100%", height: "auto", display: "block" }}
                />
              </Box>
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{ p: 3, mb: 3, border: "1px solid #e0e0e0" }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6" fontWeight={600}>
                Assessment Questions
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Chip
                  label={`${totalQuestions} Questions`}
                  size="small"
                  color="default"
                  variant="outlined"
                />
              </Box>
            </Box>
            <Divider sx={{ mb: 3 }} />

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                    <TableCell sx={{ fontWeight: 600, width: "60px" }}>
                      No.
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Question</TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        width: "100px",
                        textAlign: "center",
                      }}
                    >
                      Answer
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, width: "200px" }}>
                      Notes
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allQuestionsWithAnswers.map((question) => (
                    <TableRow
                      key={question.id}
                      sx={{
                        bgcolor: question.isAnswered ? "white" : "#fafafa",
                        "&:hover": { bgcolor: "#f0f0f0" },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {question.questionNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {question.question_text}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ textAlign: "center" }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1,
                          }}
                        >
                          {question.isAnswered && (
                            <>
                              {getAnswerIcon(question.answer)}
                              {getAnswerChip(question.answer)}
                            </>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color={
                            question.note ? "text.primary" : "text.secondary"
                          }
                          sx={{
                            fontStyle: question.note ? "normal" : "italic",
                          }}
                        >
                          {question.note || ""}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {assessment.action_required && (
            <Paper
              elevation={0}
              sx={{ p: 3, mb: 3, border: "1px solid #e0e0e0" }}
            >
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Action Required
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body1">
                {assessment.action_required}
              </Typography>
            </Paper>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button variant="contained" onClick={handleClose} sx={{ px: 4 }}>
              Close
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default EnvironmentalAssessmentDetailsModal;
